/*
 * <featured-collection-sort> — product sorting for sections/featured-collection.liquid.
 *
 * Shopify applies the `sort_by` URL parameter to the collection object of a *collection page*
 * only. A featured collection reached through `section.settings.collection` ignores it — checked
 * against the live store, where `/?sort_by=price-ascending` returns the collection's default
 * order untouched while `/collections/<handle>?sort_by=price-ascending` reorders. So this section
 * cannot render a sorted grid itself, no matter what the control is wired to.
 *
 * The sorted grid is therefore borrowed from the collection template: the collection URL with
 * `sort_by`, `page` and `section_id` is the very request the collection page's own sort control
 * makes (assets/script-facets.js), and its response holds cards rendered by the same
 * snippets/product-card.liquid. Only the <li>s are taken; this section's own <li> is reused as
 * the wrapper so grid/slider/animation classes stay whatever the section rendered.
 *
 * The two grids must agree on card shape — image_ratio, image_shape and quick_add are baked into
 * the card markup, so templates/collection.json and the featured section have to match (both
 * `square`, no quick add, today) or a sorted grid looks different from the unsorted one.
 *
 * Paging: this section paginates by `products_to_show` while the collection template paginates by
 * its own `products_per_page`, so one page here is an absolute slice of the sorted collection that
 * can span two fetched pages. Sorting never changes how many products a collection has, so the
 * server-rendered pagination stays correct and its links only need `sort_by` carried along.
 */
class FeaturedCollectionSort extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.product-grid');
    this.select = this.querySelector('select[name="sort_by"]');
    if (!this.grid || !this.select) return;

    this.pageSize = parseInt(this.dataset.pageSize, 10) || this.grid.children.length;
    this.defaultSort = this.dataset.defaultSort || this.select.value;
    this.sortValues = Array.from(this.select.options, (option) => option.value);
    this.sourcePages = new Map();
    this.sourceSectionId = null;
    this.requestId = 0;
    this.appliedSort = this.defaultSort;

    // The option list is a set of buttons (snippets/dropdown-menu.liquid), so the click is the
    // source of truth here rather than the hidden <select> that snippets/sort-dropdown.liquid
    // mirrors it into — this way the section never depends on that snippet's own script running.
    this.addEventListener('click', this.onOptionClick.bind(this));
    window.addEventListener('popstate', this.onPopState.bind(this));

    const requested = this.requestedSort();
    if (requested === this.defaultSort) {
      // The server already rendered the collection's own order, so there is nothing to fetch —
      // only the pre-paint loading state the section sets on a `sort_by` URL has to come off.
      this.setLoading(false);
    } else {
      this.apply(requested, { navigated: true });
    }
  }

  /* The sort asked for by the URL, or the collection's own order when there is none (or when the
     parameter names an option this collection doesn't offer). */
  requestedSort() {
    const requested = new URLSearchParams(window.location.search).get('sort_by');
    return requested && this.sortValues.includes(requested) ? requested : this.defaultSort;
  }

  currentPage() {
    return parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
  }

  onOptionClick(event) {
    const option = event.target.closest('.dropdown-menu__option');
    if (!option || !this.contains(option)) return;
    this.apply(option.dataset.value);
  }

  onPopState() {
    this.apply(this.requestedSort(), { navigated: true });
  }

  /* `navigated` means the URL already says what it should — a first load or a history move — so
     the URL is read rather than written, and page 1 of a fresh order is not forced. */
  apply(sortValue, { navigated = false } = {}) {
    if (!this.sortValues.includes(sortValue) || sortValue === this.appliedSort) return;

    // A new order starts at its own page 1, and pagination is server-rendered — so from a later
    // page the new sort has to travel through a real navigation to come back with correct links.
    // Anchored at the grid, like the pagination links, so the shopper doesn't land back at the top
    // of the page they were paging through.
    if (!navigated && this.currentPage() > 1) {
      const target = new URL(this.urlFor(sortValue, { resetPage: true }));
      const anchor = this.closest('.collection');
      if (anchor?.id) target.hash = anchor.id;
      window.location.assign(target.toString());
      return;
    }

    this.appliedSort = sortValue;
    this.syncDropdown(sortValue);
    this.syncPaginationLinks(sortValue);
    if (!navigated) history.pushState({}, '', this.urlFor(sortValue));

    this.render(sortValue);
  }

  urlFor(sortValue, { resetPage = false } = {}) {
    const url = new URL(window.location.href);
    if (sortValue === this.defaultSort) url.searchParams.delete('sort_by');
    else url.searchParams.set('sort_by', sortValue);
    if (resetPage) url.searchParams.delete('page');
    return url.toString();
  }

  /* Selected option, trigger label and hidden <select> in one place, so a sort restored from the
     URL looks exactly like one the shopper just picked. */
  syncDropdown(sortValue) {
    if (this.select.value !== sortValue) this.select.value = sortValue;

    const label = this.querySelector('.dropdown-menu__label');
    this.querySelectorAll('.dropdown-menu__option').forEach((option) => {
      const isSelected = option.dataset.value === sortValue;
      option.classList.toggle('dropdown-menu__option--selected', isSelected);
      option.closest('li')?.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      if (isSelected && label) label.textContent = option.textContent.trim();
    });

    this.querySelector('details[open]')?.removeAttribute('open');
  }

  syncPaginationLinks(sortValue) {
    this.querySelectorAll('.pagination__item[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (sortValue === this.defaultSort) url.searchParams.delete('sort_by');
      else url.searchParams.set('sort_by', sortValue);
      link.href = url.toString();
    });
  }

  async render(sortValue) {
    const requestId = (this.requestId += 1);
    this.setLoading(true);

    try {
      const cards = await this.cardsFor(sortValue, this.currentPage());
      if (requestId !== this.requestId) return;
      // An empty answer means the collection template couldn't be read; the cards already on
      // screen are a better fallback than an empty grid.
      if (cards.length) this.replaceCards(cards);
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === this.requestId) this.setLoading(false);
    }
  }

  /* The slice of the sorted collection this grid's current page stands for. */
  async cardsFor(sortValue, page) {
    const start = (page - 1) * this.pageSize;
    const firstPage = await this.sourcePage(sortValue, 1);
    const sourcePageSize = firstPage.length;
    if (!sourcePageSize) return [];

    const from = Math.floor(start / sourcePageSize) + 1;
    const to = Math.floor((start + this.pageSize - 1) / sourcePageSize) + 1;

    let cards = [];
    for (let sourcePage = from; sourcePage <= to; sourcePage += 1) {
      // Sequential on purpose: `to` is `from` or `from + 1`, and the second page is only worth
      // asking for once the first one has confirmed how the slice lands.
      // eslint-disable-next-line no-await-in-loop
      cards = cards.concat(sourcePage === 1 ? firstPage : await this.sourcePage(sortValue, sourcePage));
    }

    return cards.slice(start - (from - 1) * sourcePageSize, start - (from - 1) * sourcePageSize + this.pageSize);
  }

  /* Cached per sort order and page — going back to an order already seen, or paging inside one
     fetched page, costs nothing. The promise is what's cached, so two overlapping renders share
     one request; a failed one is dropped so it can be retried. */
  sourcePage(sortValue, page) {
    const key = `${sortValue}|${page}`;
    if (!this.sourcePages.has(key)) {
      this.sourcePages.set(
        key,
        this.requestCards(sortValue, page).catch((error) => {
          this.sourcePages.delete(key);
          throw error;
        }),
      );
    }
    return this.sourcePages.get(key);
  }

  async requestCards(sortValue, page) {
    // The Section Rendering API only answers to a section's *full* id — `template--<number>__<key>`,
    // where the number belongs to templates/collection.json and appears nowhere in the theme
    // source (it also differs per store and changes when the template is recreated). Asking for
    // the bare key `product-grid` is a 404. The rendered collection page carries the full id on
    // the grid, in the very attribute the collection page's own sort reads
    // (assets/script-facets.js), so the first request is the whole page and every later one is
    // just its grid section.
    if (this.sourceSectionId) {
      try {
        const cards = await this.fetchCards(this.sourceUrl(sortValue, page, this.sourceSectionId));
        if (cards.length) return cards;
      } catch (error) {
        // Republishing the collection template hands its sections new ids, so an id remembered
        // earlier in the session can stop resolving. The full page is both the fallback and where
        // a fresh id comes from.
        console.error(error);
      }
      this.sourceSectionId = null;
    }

    return this.fetchCards(this.sourceUrl(sortValue, page, null));
  }

  sourceUrl(sortValue, page, sectionId) {
    const url = new URL(this.dataset.collectionUrl, window.location.origin);
    url.searchParams.set('sort_by', sortValue);
    if (page > 1) url.searchParams.set('page', String(page));
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  async fetchCards(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Featured collection sort: ${response.status} for ${url}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = html.getElementById('product-grid');
    if (!grid) return [];

    // Both the grid <ul> and the empty-state <div> carry the grid section's full id, which is what
    // the next request needs to skip rendering the whole collection page again (see requestCards).
    if (grid.dataset.id) this.sourceSectionId = grid.dataset.id;

    // `product-grid` is the id of the collection grid <ul> *and* of the empty-state <div> it's
    // swapped for, so only list items count as cards.
    return Array.from(grid.children).filter((child) => child.tagName === 'LI');
  }

  replaceCards(cards) {
    const reference = this.grid.firstElementChild;
    if (!reference) return;

    const items = cards.map((card, index) => {
      const item = reference.cloneNode(false);
      if (item.id) item.id = item.id.replace(/-\d+$/, `-${index + 1}`);
      if (item.style.getPropertyValue('--animation-order')) {
        item.style.setProperty('--animation-order', index + 1);
      }
      // The card arrives fully rendered, so its scroll-in animation has nothing left to reveal —
      // cancelled the same way assets/script-facets.js does after a filter or sort swap.
      if (item.classList.contains('scroll-trigger')) item.classList.add('scroll-trigger--cancel');
      item.append(...card.cloneNode(true).children);
      return item;
    });

    this.grid.replaceChildren(...items);

    // A slider measures its pages from the slides it had at connect time.
    const slider = this.querySelector('slider-component');
    if (slider && typeof slider.initPages === 'function') slider.initPages();
  }

  setLoading(loading) {
    this.classList.toggle('featured-collection-sort--loading', loading);
    this.grid.setAttribute('aria-busy', loading ? 'true' : 'false');
  }
}

if (!customElements.get('featured-collection-sort')) {
  customElements.define('featured-collection-sort', FeaturedCollectionSort);
}
